import * as React from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "./SearchableSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * SelectField - Custom select component following nxplan pattern
 * @param {Object} props
 * @param {Array} props.options - Array of {key|value, label|value, hidden?, disabled?}
 * @param {string} props.value - Selected key
 * @param {function} props.onChange - Callback with synthetic event {target: {value, id}}
 * @param {function} props.onValueChange - Callback with raw value
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {string} props.emptyLabel - Disabled empty-state option label
 * @param {boolean} props.required - Show required asterisk
 * @param {boolean} props.disabled - Disable the select
 * @param {string} props.id - Element ID
 * @param {string} props.className - Container className
 * @param {string} props.selectClassName - Select button className
 * @param {string} props.labelClassName - Label className
 * @param {boolean} props.searchable - Force searchable select
 * @param {number} props.searchableThreshold - Use searchable select when options exceed threshold
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {string} props.emptyText - Empty state text for searchable select
 */
export const SelectField = ({
  options = [],
  onChange,
  onValueChange,
  value,
  placeholder = "Select an option",
  label,
  error,
  emptyLabel,
  required = false,
  disabled = false,
  id,
  className,
  selectClassName,
  labelClassName,
  searchable = false,
  searchableThreshold = 8,
  searchPlaceholder,
  emptyText,
}) => {
  const normalizedOptions = (options || [])
    .map((option) => {
      if (option == null) return null;
      if (typeof option === "string" || typeof option === "number") {
        const text = String(option);
        return { key: text, label: text, disabled: false, hidden: false };
      }
      const key = option.key ?? option.value ?? option.id ?? "";
      if (key === null || key === undefined || key === "") return null;
      return {
        key: String(key),
        label:
          option.label ??
          option.value ??
          option.key ??
          option.id ??
          String(key),
        disabled: Boolean(option.disabled),
        hidden: Boolean(option.hidden),
      };
    })
    .filter(Boolean)
    .filter((option) => !option.hidden);

  const displayOptions =
    normalizedOptions.length === 0 && emptyLabel
      ? [
          {
            key: "__empty",
            label: emptyLabel,
            disabled: true,
            hidden: false,
          },
        ]
      : normalizedOptions;

  const shouldSearch =
    searchable || normalizedOptions.length > searchableThreshold;

  const normalizedValueForSelect =
    value === "" || value === null || typeof value === "undefined"
      ? undefined
      : value;

  const handleValueChange = (selectedValue) => {
    onValueChange?.(selectedValue);
    if (!onChange) return;
    const syntheticEvent = {
      target: {
        value: selectedValue,
        id,
      },
    };
    onChange(syntheticEvent);
  };

  return (
    <div className={cn("space-y-2", className)} id={id}>
      {label && (
        <Label className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {shouldSearch ? (
        <SearchableSelect
          options={normalizedOptions.map((option) => ({
            value: option.key,
            label: option.label,
            disabled: option.disabled,
          }))}
          value={value || ""}
          onChange={handleValueChange}
          placeholder={placeholder}
          emptyText={emptyText || emptyLabel}
          searchPlaceholder={searchPlaceholder}
          disabled={disabled}
          className={cn(error && "border-destructive", selectClassName)}
        />
      ) : (
        <Select
          value={normalizedValueForSelect}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              "w-full",
              error && "border-destructive",
              selectClassName,
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent position="popper">
            {displayOptions.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {error && <small className="text-destructive text-xs">{error}</small>}
    </div>
  );
};
